import { useEffect, useMemo, useRef, useState } from "react"

import { Menu, MenuButton, Radio, RadioGroup } from "@headlessui/react"
import { CheckIcon, ChevronDownIcon, ChevronUpIcon, XMarkIcon } from "@heroicons/react/20/solid"
import { twMerge } from "tailwind-merge"
import { humanize } from "underscore.string"

import { Bee } from "~/src/components/BeeKit"

//region Types
type SortMenuPropsBase = {
  sortOptions: SortOptions
  className?: string
  buttonClassName?: string
  menuClassName?: string
  onSelect?: (option: SortOption) => void
  children?: React.ReactNode
}

type UncontrolledSortMenuProps = SortMenuPropsBase & {
  controlled?: false
  value?: SortOption
  buttonText?: string
}

type ControlledSortMenuProps = SortMenuPropsBase & {
  controlled: true
  value: SortOption
  buttonLabel?: string
  isMenuOpen: boolean
  toggleMenu: () => void
  closeMenu: () => void
  handleSelection: (option: SortOption) => void
  ChevronIcon: React.ComponentType<React.SVGProps<SVGSVGElement>>
}

export type SortMenuProps = UncontrolledSortMenuProps | ControlledSortMenuProps

export type SortOptions = {
  default: string
  options: SortOption[]
}

export type SortOption = {
  label: string
  field: string
  direction: "asc" | "desc"
}
//endregion

//region Shared Hook
export function useSortMenuState(props: SortMenuProps) {
  const { sortOptions, onSelect } = props

  const options = sortOptions.options

  const defaultValue = options.find((option) => option.label == sortOptions.default) ?? options?.[0]
  const value = props.value ?? defaultValue

  const [isMenuOpen, setMenuOpen] = useState(false)

  const toggleMenu = () => setMenuOpen((prev) => !prev)
  const closeMenu = () => setMenuOpen(false)

  const handleSelection = (option: SortOption) => {
    onSelect?.(option)
    closeMenu()
  }

  // UI state
  const buttonLabel = useMemo(() => options.find((option) => optionIsEqual(option, value))?.label, [options, value])
  const ChevronIcon = isMenuOpen ? ChevronUpIcon : ChevronDownIcon

  return {
    buttonLabel,
    value,
    isMenuOpen,
    toggleMenu,
    closeMenu,
    handleSelection,
    ChevronIcon,
  }
}
//endregion

//region Main Components
export function DropdownMenu(props: SortMenuProps) {
  const { sortOptions, className, buttonClassName, menuClassName, children } = props

  const { value, isMenuOpen, toggleMenu, closeMenu, handleSelection, buttonLabel, ChevronIcon } =
    props.controlled === true
      ? {
          value: props.value,
          isMenuOpen: props.isMenuOpen,
          toggleMenu: props.toggleMenu,
          closeMenu: props.closeMenu,
          handleSelection: props.handleSelection,
          buttonLabel: props.buttonLabel,
          ChevronIcon: props.ChevronIcon,
        }
      : useSortMenuState(props)

  const sortByRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  // Outside click detection
  useEffect(() => {
    if (!isMenuOpen) return

    const handleOutsideClick = (event: MouseEvent) => {
      const path = event.composedPath()

      if (
        menuRef.current &&
        sortByRef?.current &&
        !path.includes(menuRef.current) &&
        !path.includes(sortByRef.current)
      ) {
        closeMenu()
      }
    }

    document.addEventListener("mousedown", handleOutsideClick)
    return () => document.removeEventListener("mousedown", handleOutsideClick)
  }, [isMenuOpen, closeMenu])

  return (
    <div ref={sortByRef} className={twMerge("relative flex items-center pb-2 text-sm", className)}>
      {children ? (
        children
      ) : (
        <button className={twMerge("flex cursor-pointer items-center gap-1", buttonClassName)} onClick={toggleMenu}>
          <span className="font-bold">Sort by</span>
          {buttonLabel}
          <ChevronIcon className="h-5 w-5" />
        </button>
      )}

      {isMenuOpen && (
        <div
          ref={menuRef}
          className={twMerge(
            "absolute top-full right-0 z-50 mt-1.5 min-w-[219px] flex-col items-start divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white shadow-lg",
            menuClassName
          )}
        >
          <Menu>
            {sortOptions.options.map((option) => (
              <MenuButton
                key={displaySortOption(option)}
                className="flex w-full items-center px-4 py-3 text-left text-sm hover:cursor-pointer"
                onClick={() => handleSelection(option)}
              >
                {optionIsEqual(value, option) && <CheckIcon className="mr-2 h-4 w-4 text-blue-600" />}
                {option.label}
              </MenuButton>
            ))}
          </Menu>
        </div>
      )}
    </div>
  )
}

export function FullScreenMenu(props: SortMenuProps) {
  const { sortOptions, className, buttonClassName, menuClassName, children } = props

  const { value, isMenuOpen, toggleMenu, closeMenu, handleSelection } =
    props.controlled === true
      ? {
          value: props.value,
          isMenuOpen: props.isMenuOpen,
          toggleMenu: props.toggleMenu,
          closeMenu: props.closeMenu,
          handleSelection: props.handleSelection,
        }
      : useSortMenuState(props)

  // Internal selection state for the "Apply" pattern
  const [selectedValue, setSelectedValue] = useState<SortOption>(value)

  // Update internal selection when controlled value changes
  useEffect(() => {
    setSelectedValue(value)
  }, [value])

  const handleApply = () => {
    handleSelection(selectedValue)
  }

  return (
    <div className={twMerge("relative flex items-center pb-2 text-sm", className)}>
      {children ? (
        children
      ) : (
        <Bee.Button className={buttonClassName} onClick={toggleMenu}>
          Sort
        </Bee.Button>
      )}

      {isMenuOpen && (
        <div className={twMerge("flex flex-col overflow-hidden bg-white", menuClassName)}>
          <div className="flex justify-between px-4 py-3.5">
            <h1 className="text-lg font-bold">Sort By</h1>
            <XMarkIcon onClick={closeMenu} className="ml-1 h-6 w-6 text-gray-600" />
          </div>

          <hr />

          <RadioGroup className="flex grow flex-col px-6 pb-8">
            {sortOptions.options.map((option) => (
              <Radio
                key={option.label}
                value={option}
                className="flex items-center gap-2 py-2.5"
                onClick={() => setSelectedValue(option)}
              >
                <div
                  className={twMerge(
                    "flex h-4 w-4 rounded-full",
                    optionIsEqual(selectedValue, option) ? "border-navy-800 border-4" : "border border-gray-300"
                  )}
                />
                {option.label}
              </Radio>
            ))}
            <div className="mt-auto flex">
              <Bee.Button kind="primary" className="w-full" onClick={handleApply}>
                Apply
              </Bee.Button>
            </div>
          </RadioGroup>
        </div>
      )}
    </div>
  )
}
//endregion

//region Private Functions
function optionIsEqual(option?: SortOption, selectedOption?: SortOption) {
  if (option == null || selectedOption == null) return false

  return option?.field === selectedOption?.field && option?.direction === selectedOption?.direction
}

function displaySortOption(option: SortOption) {
  return option?.label ?? humanize(`${option?.field} ${option?.direction}`)
}
//endregion
